import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { TarifPageVariant } from './types';

interface UseUnifiedTarifConfigProps {
    variant: TarifPageVariant;
}

export function useUnifiedTarifConfig({ variant }: UseUnifiedTarifConfigProps) {
    const tarifData = useFormContext().watch();

    return useMemo(() => {
        const Tarif = tarifData.Tarif;

        if (!Tarif?.ListeFormules) {
            return {
                formules: [],
                garanties: variant.garantiesConfig,
                garantiesExclues: variant.garantiesExclues || [],
                documents: [],
                textes: variant.textes,
            };
        }

        // Enrichissement des garanties avec liens dynamiques
        const garantiesEnrichies = variant.garantiesConfig.map(garantie => {
            let description = garantie.description;

            // Si mention de vétusté contractuelle ET URL disponible
            if (description.includes('de sa vétusté contractuelle') && Tarif.UrlPDFDispositionsGenerales) {
                description = description.replace(
                    /de sa vétusté contractuelle/g,
                    `de sa vétusté contractuelle (voir les <a href="${Tarif.UrlPDFDispositionsGenerales}" target="_blank" rel="noopener noreferrer">Dispositions Générales</a>)`
                );
            }

            return {
                ...garantie,
                description,
            };
        });

        // Tri des formules avec F1 toujours en premier
        const formulesTriees = [...Tarif.ListeFormules].sort((a, b) => {
            if (a.CodeFormule === 'F1') return -1;
            if (b.CodeFormule === 'F1') return 1;
            if (a.CodeFormule === 'F2') return -1;
            if (b.CodeFormule === 'F2') return 1;
            return a.CodeFormule.localeCompare(b.CodeFormule);
        });

        const formules = formulesTriees.map((formule) => {
            const metaConfig = variant.formulesConfigMeta.find((meta) => meta.code === formule.CodeFormule) || {
                code: formule.CodeFormule,
                description: `Formule ${formule.LibelleFormule}`,
                recommande: false,
            };

            return {
                id: formule.CodeFormule,
                nom: formule.LibelleFormule,
                description: metaConfig.description,
                recommande: metaConfig.recommande,
                tarif: {
                    mensuel: formule.PrimeMensuelleBase,
                    annuel: formule.PrimeAnnuelleFracAnnuelBase,
                    mensuelPromo: formule.PrimeMensuelleBasePromo,
                    annuelPromo: formule.PrimeAnnuelleFracAnnuelBasePromo,
                },
                garanties: formule.ListeGaranties || [],
            };
        });

        let documents: { nom: string; url: string }[] = [];

        if (variant.variant === 'amo') {
            documents = [
                { nom: "Proposition d'assurance", url: Tarif.UrlPDFDevis },
                { nom: 'Dispositions Générales', url: Tarif.UrlPDFDispositionsGenerales },
                { nom: "Convention d'Assistance", url: Tarif.UrlPDFConventionAssistance },
                { nom: "Document d'information", url: Tarif.UrlPDFDocumentInformation },
                { nom: 'Lettre RGPD', url: Tarif.UrlPDFLettreRGPD },
            ];
        } else if (variant.variant === 'mae') {
            documents = [
                { nom: "Proposition d'assurance", url: Tarif.UrlPDFDevis },
                { nom: 'Dispositions Générales', url: Tarif.UrlPDFDispositionsGenerales },
                { nom: 'Convention d’Assistance', url: Tarif.UrlPDFConventionAssistance },
                { nom: "Document d'information sur le produit Trottinettes et Plus", url: Tarif.UrlPDFDocumentInformation },
                { nom: "Document d'information sur les garanties d'Assistance", url: Tarif.UrlPDFDocumentInformationAssistance },
                { nom: 'Lettre RGPD', url: Tarif.UrlPDFLettreRGPD },
            ];
        }

        documents = documents.filter((doc) => Boolean(doc.url));

        // Gestion des options selon la configuration du variant
        const options = [];

        if (variant.optionCode && variant.optionIndividuelleConfig) {
            const formuleOption = variant.optionFormuleTarget || 'F2';
            const formule = formulesTriees.find((f) => f.CodeFormule === formuleOption) || formulesTriees[0];

            if (formule?.ListeGaranties) {
                const option = formule.ListeGaranties.find((g: { CodeGarantie: string }) => g.CodeGarantie === variant.optionCode);
                if (option) {
                    options.push({
                        code: variant.optionCode,
                        nom: option.LibelleGarantie || variant.optionIndividuelleConfig.nom || variant.optionIndividuelleConfig.description,
                        description: variant.optionIndividuelleConfig.description,
                        tarif: {
                            mensuel: option.PrimeClientMensuelle || 0,
                            annuel: option.PrimeClient || 0,
                            mensuelPromo: option.PrimeClientMensuellePromo,
                            annuelPromo: option.PrimeClientPromo,
                        },
                        avantages: variant.optionIndividuelleConfig.avantages,
                        exclusiveToFormule: formuleOption !== 'F1' ? formuleOption : undefined,
                    });
                }
            }
        }

        // Vérifier si un code promo est actif et valide
        const codePromoActif = Boolean(Tarif.CodePromo?.Eligible);

        return {
            formules,
            garanties: garantiesEnrichies,
            garantiesExclues: variant.garantiesExclues || [],
            options,
            documents,
            textes: variant.textes,
            fraisDossier: Tarif.MontantFraisDossier,
            taxeAttentat: formulesTriees.find((f) => f.CodeFormule === 'F1')?.MontantTaxeAttentat,
            codePromoActif,
        };
    }, [variant, tarifData]);
}
